// Module: SocialIcon
// Role: Rounded badge for social / marketplace platform (WB, Ozon, VK, TT).
//       Done state = success green + check icon; active = terracotta shadow.
// Uses: P tokens, Check icon
// Used by: PublishOverviewScreen
// Design source: pantene-flow.jsx:1184 (SocialIcon function)

import { P, T } from '../../../styles/tokens';
import { Check } from './Icons';

// NOTE: 'vkontakte' (not 'vk') deliberately — lowercase 'vk' is the reserved
// runtime-platform API code (ADR-023/ADR-025) and is banned outside src/platform/.
// These keys identify the SOCIAL NETWORK / marketplace, not the miniapp host platform.
export type SocialPlatform = 'wb' | 'ozon' | 'vkontakte' | 'tt';

interface SocialIconProps {
  /** Platform key. */
  platform: SocialPlatform;
  /** Whether this publish step is completed. */
  done: boolean;
  /** Whether this is the currently active step. */
  active: boolean;
}

const PLATFORM_STYLES: Record<SocialPlatform, { bg: string; text: string }> = {
  wb:        { bg: '#CB11AB', text: 'WB' },
  ozon:      { bg: '#005BFF', text: 'OZ' },
  vkontakte: { bg: '#0077FF', text: 'VK' },
  tt:        { bg: '#000000', text: 'TT' },
};

/**
 * Derive platform key from a checkpoint slug.
 * Falls back to 'vkontakte' for unknown slugs.
 */
export function platformFromSlug(slug: string): SocialPlatform {
  const s = slug.toLowerCase();
  if (s.includes('ozon')) return 'ozon';
  if (s.includes('wb') || s.includes('wildberries')) return 'wb';
  if (s.includes('tt') || s.includes('tiktok')) return 'tt';
  // promo_link, social_* -> default to VK (Pantene Format B default)
  return 'vkontakte';
}

const SIZE = 36;

export function SocialIcon({ platform, done, active }: SocialIconProps) {
  const style = PLATFORM_STYLES[platform] ?? PLATFORM_STYLES.vkontakte;
  return (
    <div
      data-testid={`social-icon-${platform}`}
      style={{
        width: SIZE,
        height: SIZE,
        borderRadius: 10,
        flexShrink: 0,
        background: done ? P.success : style.bg,
        color: '#fff',
        fontFamily: T.fontDisplay,
        fontWeight: 700,
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: active ? '0 4px 12px rgba(192,81,58,0.20)' : 'none',
        transition: 'all 0.25s ease',
      }}
    >
      {done ? <Check size={18} /> : style.text}
    </div>
  );
}
